
import { removeEmptyValuesInObject } from '@core/helpers'
import { createObserver } from '@core/helpers/vue-Intersection-observer'
import { saveProductInfos } from '@tracking'
import { trackProductImpression } from '@tracking/events'

import { Card, CardPlaceholder } from './Card'
import { List, ListPlaceholder } from './List'

export const PRODUCT_DISPLAYS = {
  CARD: 'card',
  LIST: 'list',
}

/** @deprecated */
export default {
  props: {
    productId: {
      type: [String, Number],
      default: null,
    },
    listingId: {
      type: [String, Number],
      default: null,
    },
    link: {
      type: Object,
      default: () => {},
    },
    title: {
      type: String,
      default: '',
    },
    titleModel: {
      type: String,
      default: '',
    },
    subTitleModel: {
      type: Array,
      default: () => [],
    },
    brand: {
      type: String,
      default: '',
    },
    grade: {
      type: String,
      default: '',
    },
    warranty: {
      type: [String, Number],
      default: '',
    },
    thumbnail: {
      type: String,
      default: '',
    },
    attributes: {
      type: Array,
      default: () => [],
    },
    name: {
      type: String,
      default: '',
    },
    /**
     * Before Backmarket's price
     *
     * @typedef {object} price
     * @property {number} price.raw
     * @property {string} price.display
     */
    price: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Before Backmarket's price
     *
     * @typedef {object} originalPrice
     * @property {number} originalPrice.raw
     * @property {string} originalPrice.display
     */
    originalPrice: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Carriers Details
     *
     * @typedef {object} carriers
     * @property {Array} carriers.list
     * @property {Boolean} carriers.locked
     * @property {Boolean} carriers.showCarrier
     */
    carriers: {
      type: Object,
      default: () => ({}),
    },
    category: {
      type: String,
      default: '',
    },
    // 'currency' can be null only if 'price' and 'shippingPrice' are not set as well.
    currency: {
      type: String,
      default: null,
    },
    display: {
      type: String,
      default: PRODUCT_DISPLAYS.CARD,
      validator: (value) => Object.values(PRODUCT_DISPLAYS).includes(value),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    sourceType: {
      type: String,
      default: '',
    },
    isRecommendation: {
      type: Boolean,
      default: false,
    },
    sellerId: {
      type: String,
      default: '',
    },
    stockRaw: {
      type: Number,
      default: 0,
    },
    provider: {
      type: String,
      default: '',
    },
    variant: {
      type: Number,
      default: 0,
    },
    position: {
      type: Number,
      default: 1,
    },
    isOriginalPriceDisplayed: {
      type: Boolean,
      default: false,
    },
    disabledSelfImpressionEvent: {
      type: Boolean,
      default: false,
    },
    productAlgoliaReranked: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return { unobserve: null }
  },

  computed: {
    cardComponent() {
      return this.isLoading ? CardPlaceholder : Card
    },
    listComponent() {
      return this.isLoading ? ListPlaceholder : List
    },
    productComponent() {
      switch (this.display) {
        case PRODUCT_DISPLAYS.CARD:
          return this.cardComponent
        case PRODUCT_DISPLAYS.LIST:
          return this.listComponent
        default:
          return null
      }
    },
    propsToBind() {
      const {
        link,
        attributes,
        category,
        name,
        title,
        titleModel,
        subTitleModel,
        thumbnail,
        price,
        originalPrice,
        currency,
        warranty,
        grade,
        carriers,
        isRecommendation,
        isOriginalPriceDisplayed,
      } = this.$props

      const cardProps = {
        link,
        name,
        category,
        title,
        titleModel,
        subTitleModel,
        thumbnail,
        price,
        originalPrice,
        currency,
        warranty,
        grade,
        carriers,
        isRecommendation,
        isOriginalPriceDisplayed,
      }

      // Remove empty props and bind right props to the right component to prevent polluting DOM
      if (this.display === PRODUCT_DISPLAYS.CARD) {
        return removeEmptyValuesInObject(cardProps)
      }
      if (this.display === PRODUCT_DISPLAYS.LIST) {
        return {
          ...removeEmptyValuesInObject({
            ...cardProps,
            attributes,
          }),
          attributes,
        }
      }

      return {}
    },

    // FIXME: User an adapter here
    productTrackingInfos() {
      return {
        list: this.sourceType,
        brand: this.brand,
        category: this.category,
        stock: this.stockRaw,
        price: this.price.raw,
        currency: this.currency,
        id: this.productId,
        // TODO: URL-352 Use product.id when available in Algolia to get the UUID
        uuid: this.link?.params.uuid,
        warrantyDuration: this.warranty,
        name: this.title,
        variant: this.variant,
        provider: this.provider,
        source: this.sourceType,
        merchantId: this.sellerId,
        model: this.titleModel,
        listingId: this.listingId,
        position: this.position,
        productAlgoliaReranked: this.productAlgoliaReranked,
      }
    },
  },

  destroyed() {
    if (this.unobserve) {
      this.unobserve()
    }
  },

  mounted() {
    this.watchCardInViewport()
  },

  methods: {
    watchCardInViewport() {
      // Don't observe anything if the component is in a loading state.
      if (this.isLoading) {
        return
      }

      this.unobserve = createObserver(this.$el, () => {
        if (!this.disabledSelfImpressionEvent) {
          trackProductImpression(this.productTrackingInfos)
        }

        this.$emit('viewport-impression', this.productTrackingInfos)
      })
    },

    storeItem() {
      saveProductInfos({
        id: this.productId,
        price: this.price.raw,
        source: this.sourceType,
      })
    },
  },
}
